<template>
  <div>
    <load-profile />
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>
    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-2">
        <b-row>
          <b-col
            class="d-flex align-items-center"
            cols="8"
          >
            <div>
              <span>{{ $t('general.entries') }}</span>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-50 mr-1"
              >
                <template #no-options>
                  {{ $t("selection.noOption") }}
                </template>
              </v-select>
            </div>
          </b-col>

          <b-col
            v-if="$can('create', permissionType)"
            class="text-right"
            cols="4"
          >
            <b-button
              variant="primary"
              @click="openCreateModal"
            >
              {{ $t('button.add_employee') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(name)="data">
            {{ data.item.name }}
            <b-badge
              v-if="data.item.isBot == true"
              pill
              variant="info"
              badge-glow
            >
              bot
            </b-badge>
          </template>

          <template #cell(isActive)="data">
            <b-badge
              v-if="data.item.isActive"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`status.active`) }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="danger"
              badge-glow
            >
              {{ $t(`status.inactive`) }}
            </b-badge>
          </template>
          <template #cell(isManager)="data">
            <b-badge
              v-if="data.item.isManager"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`field.manager`) }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="info"
              badge-glow
            >
              {{ $t(`field.member`) }}
            </b-badge>
          </template>
          <template #cell(department)="data">
            <span v-if="data.item.department">
              {{ data.item.department.name }}
            </span>
          </template>
          <template #cell(workSchedule)="data">
            <span v-if="data.item.workSchedule">
              {{ data.item.workSchedule.title }}
            </span>
          </template>
          <template #cell(projects)="data">
            <li v-for="project in data.item.project" :key="project.id">
              {{ getProjectName(project) }}
            </li>
          </template>
          <template #cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                v-if="$can('update', permissionType)"
                v-b-tooltip.hover
                variant="primary"
                pill
                size="sm"
                :title="$t('general.update')"
                @click="openEditModal(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="$can('delete', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.delete')"
                variant="danger"
                pill
                size="sm"
                @click="confirmDestroy(data.item.id, data.index, data.item)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>

    <!-- Form Modal -->
    <form-modal
      ref="formModal"
      :is-edit="isEdit"
      :item="selectedItem"
      @submit="handleSubmit"
      @hide="handleModalHide"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BFormGroup,
  BInputGroup,
  BAvatar,
  BBadge,
  BLink,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination'
import NSearchContainer from '@/components/NSearchContainer'
import NSearchInput from '@/components/NSearchInput'
import NTable from '@/components/NTable'
import { avatarText } from '@core/utils/filter'
import SearchInputs from './searchInput'
import tableFields from './tableField'
import FormModal from './FormModal.vue'

const EmployeeRepository = Repository.get('employee')
const DepartmentRepository = Repository.get('department')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BAvatar,
    BLink,
    BFormGroup,
    BInputGroup,
    BBadge,
    BSpinner,
    NPagination,
    NSearchInput,
    NSearchContainer,
    NTable,
    vSelect,
    FormModal,
  },
  data() {
    return {
      items: [],
      loading: false,
      resendLoading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        isActive: this.$route.query.isActive || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        isActive: this.$route.query.isActive || null,
        departmentId: this.$route.params.departmentId,
      },
      isEdit: false,
      selectedItem: null,
      avatarText,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    activeSearch() {
      return !!this.$route.query.name || !!this.$route.query.isEnable
    },
  },
  watch: {
    perPage(value) {
      this.list(1)
    },
  },
  mounted() {
    this.getData(this.query.page)
  },
  methods: {
    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQuerySting()
      this.getData()
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    getData() {
      this.loading = true
      EmployeeRepository.list({
        ...this.query,
      })
        .then(response => {
          const data = response.data.data.list
          this.items = [...data]
          this.total = response.data.data.total
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    confirmDestroy(id, index, data) {
      index = this.$refs.table.calculateOrderNumber(index)

      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.unableToRevertAfterDelete', {
            item: `#${index} ${data.name}`,
          }),
          {
            title: this.$t('alert.areYouSure'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('button.yesDelete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.destroy(id)
          }
        })
    },
    destroy(id) {
      EmployeeRepository.destroy(id)
        .then(response => {
          this.getData()
        })
        .catch(error => {})
        .then(() => {
          this.loading = false
        })
    },
    formatTime(dateTime) {
      const date = new Date(dateTime)
      const hours = date.getHours()
      const minutes = date.getMinutes()

      // Convert to 12-hour format
      const ampm = hours >= 12 ? 'PM' : 'AM'
      const formattedHours = hours % 12 || 12 // handle midnight and noon

      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes
      return `${formattedHours}:${formattedMinutes} ${ampm}`
    },
    getWorkingDays(workingDays) {
      if (Array.isArray(workingDays) && workingDays.length > 0) {
        return workingDays.map(day => this.$t(`dayName.${day}`)).join(', ')
      }
      return '-'
    },
    openCreateModal() {
      this.isEdit = false
      this.selectedItem = null
      this.$nextTick(() => {
        this.$refs.formModal.show()
      })
    },
    openEditModal(item) {
      this.isEdit = true
      this.selectedItem = {
        ...item,
      }
      this.$nextTick(() => {
        this.$refs.formModal.show()
      })
    },
    async handleSubmit(formData) {
      try {
        if (this.isEdit) {
          await EmployeeRepository.update(formData.id, formData)
        } else {
          await EmployeeRepository.create(formData)
        }
        this.$refs.formModal.hide()
        this.getData()
      } catch (error) {
        console.error('Error submitting employee:', error)
      }
    },
    handleModalHide() {
      this.selectedItem = null
      this.isEdit = false
    },
    getProjectName(data) {
      if (this.$i18n.locale === 'vi') {
        return data?.name?.vi ? data?.name?.vi : data?.name?.en
      }
      if (this.$i18n.locale === 'km') {
        return data?.name?.km ? data?.name?.km : data?.name?.en
      }
      if (this.$i18n.locale === 'en') {
        return data?.name?.en ? data?.name?.en : data?.name?.zh
      }
      return data?.name?.zh ? data?.name?.zh : data?.name?.en
    },
    getProjects(projects) {
      if (!projects || !projects.length) {
        return '-'
      }

      return projects.map(project => this.getProjectName(project)).join('<br>')
    },
  },
  async beforeRouteEnter(to, from, next) {
    if (!to.params.departmentId) {
      return next({ name: 'list-department' })
    }

    try {
      const response = await DepartmentRepository.show(
        to.params.departmentId,
      )
      const data = response?.data?.data

      if (!data || !data.id) {
        // If no data or invalid data, redirect back to department list
        return next({ name: 'list-department' })
      }

      // Set breadcrumb if data exists
      to.meta.breadcrumb = [
        {
          text: 'breadcrumb.list_department',
          to: { name: 'list-department' },
        },
        {
          text: data.name,
          active: true,
        },
      ]

      next()
    } catch (error) {
      // Handle any errors by redirecting to department list
      console.error('Failed to fetch department:', error)
      next({ name: 'list-department' })
    }
  },
  setup() {
    const fields = tableFields
    const searchFields = SearchInputs
    const permissionType = 'manage-users'
    const route = 'employee'

    return {
      fields,
      searchFields,
      permissionType,
      route,
    }
  },
}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
</style>
